import { useApp } from '../context.js';
import { Star, TrendingUp, Trophy } from '../icons.jsx';
import RecapCard from './RecapCard.jsx';

export default function StatsTab() {
    const { ratedCount, stats, t } = useApp();
    return (
        <div className="space-y-4">
            {/* Our Year in Restaurants */}
            <RecapCard />

            {/* Battle Stats */}
            <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Trophy className="text-yellow-500" size={24} />
                    Check Battle Champion
                </h2>
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                        <h3 className="text-base font-bold text-blue-800">Doug</h3>
                        <div className="text-3xl font-bold text-blue-600 mt-2">{stats.dougWins}</div>
                        <div className="text-xs text-blue-600 font-medium">Wins</div>
                        <div className="text-xs text-gray-600 mt-2">Avg ±${stats.dougAvgDiff}</div>
                    </div>
                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-xl">
                        <h3 className="text-base font-bold text-pink-800">Les</h3>
                        <div className="text-3xl font-bold text-pink-600 mt-2">{stats.lesWins}</div>
                        <div className="text-xs text-pink-600 font-medium">Wins</div>
                        <div className="text-xs text-gray-600 mt-2">Avg ±${stats.lesAvgDiff}</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-xl text-center">
                        <div className="text-2xl font-bold text-gray-700">{stats.totalChecks}</div>
                        <div className="text-xs text-gray-600">Total Battles</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl text-center">
                        <div className="text-2xl font-bold text-gray-700">{stats.ties}</div>
                        <div className="text-xs text-gray-600">Ties</div>
                    </div>
                </div>
            </div>

            {/* Money Stats */}
            {stats.totalChecks > 0 && (
                <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        💰 Money Stats
                    </h2>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl text-center">
                            <div className="text-2xl font-bold text-green-600">${stats.totalSpent.toFixed(2)}</div>
                            <div className="text-xs text-gray-600">Total Spent</div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl text-center">
                            <div className="text-2xl font-bold text-green-600">${stats.avgCheck}</div>
                            <div className="text-xs text-gray-600">Avg Check</div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className={`${t.accentBg} p-3 rounded-xl`}>
                            <div className="text-xs text-gray-600 mb-1">💸 Most Expensive</div>
                            <div className={`font-bold ${t.accentText}`}>${stats.highestCheck.amount.toFixed(2)}</div>
                            <div className="text-xs text-gray-600">{stats.highestCheck.restaurant}</div>
                        </div>
                        {stats.lowestCheck.amount !== Infinity && (
                            <div className="bg-blue-50 p-3 rounded-xl">
                                <div className="text-xs text-gray-600 mb-1">💵 Best Deal</div>
                                <div className="font-bold text-blue-700">${stats.lowestCheck.amount.toFixed(2)}</div>
                                <div className="text-xs text-gray-600">{stats.lowestCheck.restaurant}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Best & Worst Guesses */}
            {stats.totalChecks > 0 && (
                <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        🎯 Best & Worst Guesses
                    </h2>

                    <div className="space-y-3">
                        {/* Best Guesses */}
                        <div>
                            <div className="text-sm font-bold text-green-700 mb-2">🏆 Most Accurate</div>
                            <div className="grid grid-cols-2 gap-2">
                                {stats.dougBestGuess.diff !== Infinity && (
                                    <div className="bg-blue-50 p-3 rounded-lg">
                                        <div className="text-xs font-bold text-blue-700">Doug</div>
                                        <div className="text-lg font-bold text-blue-600">±${stats.dougBestGuess.diff.toFixed(2)}</div>
                                        <div className="text-xs text-gray-600">{stats.dougBestGuess.restaurant}</div>
                                        <div className="text-xs text-gray-500">${stats.dougBestGuess.amount.toFixed(2)} check</div>
                                    </div>
                                )}
                                {stats.lesBestGuess.diff !== Infinity && (
                                    <div className="bg-pink-50 p-3 rounded-lg">
                                        <div className="text-xs font-bold text-pink-700">Les</div>
                                        <div className="text-lg font-bold text-pink-600">±${stats.lesBestGuess.diff.toFixed(2)}</div>
                                        <div className="text-xs text-gray-600">{stats.lesBestGuess.restaurant}</div>
                                        <div className="text-xs text-gray-500">${stats.lesBestGuess.amount.toFixed(2)} check</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Worst Guesses */}
                        <div>
                            <div className="text-sm font-bold text-red-700 mb-2">😅 Furthest Off</div>
                            <div className="grid grid-cols-2 gap-2">
                                {stats.dougWorstGuess.diff > 0 && (
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <div className="text-xs font-bold text-gray-700">Doug</div>
                                        <div className="text-lg font-bold text-red-600">±${stats.dougWorstGuess.diff.toFixed(2)}</div>
                                        <div className="text-xs text-gray-600">{stats.dougWorstGuess.restaurant}</div>
                                        <div className="text-xs text-gray-500">${stats.dougWorstGuess.amount.toFixed(2)} check</div>
                                    </div>
                                )}
                                {stats.lesWorstGuess.diff > 0 && (
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <div className="text-xs font-bold text-gray-700">Les</div>
                                        <div className="text-lg font-bold text-red-600">±${stats.lesWorstGuess.diff.toFixed(2)}</div>
                                        <div className="text-xs text-gray-600">{stats.lesWorstGuess.restaurant}</div>
                                        <div className="text-xs text-gray-500">${stats.lesWorstGuess.amount.toFixed(2)} check</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Restaurant Stats */}
            <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    🍽️ Restaurant Stats
                </h2>

                {stats.mostVisited.count > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl mb-3">
                        <div className="text-xs text-gray-600 mb-1">👑 Most Visited</div>
                        <div className="text-lg font-bold text-purple-700">{stats.mostVisited.name}</div>
                        <div className="text-sm text-gray-600">{stats.mostVisited.count} visits</div>
                    </div>
                )}

                {stats.topRated.length > 0 && (
                    <div>
                        <div className="text-sm font-bold text-gray-700 mb-2">⭐ Top Rated Places</div>
                        <div className="space-y-2">
                            {stats.topRated.map((place, idx) => (
                                <div key={idx} className={`${t.accentBg} p-3 rounded-lg flex items-center justify-between`}>
                                    <div className="flex-1">
                                        <div className="font-semibold text-gray-800 text-sm">{place.name}</div>
                                    </div>
                                    <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-full">
                                        <Star size={12} className="text-yellow-500 fill-yellow-500" />
                                        <span className="text-sm font-bold text-gray-700">{place.rating}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Rating Distribution */}
            {ratedCount > 0 && (
                <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        📊 Rating Distribution
                    </h2>
                    <div className="space-y-2">
                        {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(rating => {
                            const count = stats.ratingDistribution[rating];
                            const total = ratedCount;
                            const percentage = total > 0 ? (count / total) * 100 : 0;

                            if (count === 0) return null;

                            return (
                                <div key={rating} className="flex items-center gap-2">
                                    <div className="w-8 text-sm font-bold text-gray-700">{rating}</div>
                                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                                        <div 
                                            className={`bg-gradient-to-r ${t.accent} h-full flex items-center justify-end pr-2`}
                                            style={{ width: `${percentage}%` }}
                                        >
                                            <span className="text-xs font-bold text-white">{count}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recent History */}
            <div className="bg-white/90 apple-card rounded-2xl p-5 shadow-md">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="text-green-500" size={24} />
                    Recent Check History
                </h2>
                <div className="space-y-3">
                    {stats.allChecks.slice(0, 8).map((check) => (
                        <div key={check.id} className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-bold text-gray-800 text-sm">{check.restaurantName}</div>
                                <div className="text-xs text-gray-500">
                                    {new Date(check.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className={`p-2 rounded-lg ${check.winner === 'doug' ? 'bg-blue-100 border-2 border-blue-400' : 'bg-white'}`}>
                                    <div className="font-bold text-blue-600">Doug</div>
                                    <div className="font-mono">${check.dougGuess.toFixed(2)}</div>
                                    <div className="text-gray-600">±${check.dougDiff.toFixed(2)}</div>
                                </div>
                                <div className="p-2 rounded-lg bg-yellow-50 text-center">
                                    <div className="font-bold text-gray-600">Actual</div>
                                    <div className="text-base font-bold text-green-600">${check.actualAmount.toFixed(2)}</div>
                                </div>
                                <div className={`p-2 rounded-lg ${check.winner === 'les' ? 'bg-pink-100 border-2 border-pink-400' : 'bg-white'}`}>
                                    <div className="font-bold text-pink-600">Les</div>
                                    <div className="font-mono">${check.lesGuess.toFixed(2)}</div>
                                    <div className="text-gray-600">±${check.lesDiff.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {stats.totalChecks === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <p className="font-medium">No check guesses yet!</p>
                            <p className="text-sm mt-2">Start playing 📊</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
